import { useEffect, useState } from "react";
import { Buildings, Gear, Money, Bell, ClipboardText, Plus, Trash } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { WrapQuestionBank } from "../components/WrapQuestionBank";

const FIELDS = [
  ["office_hours", "Office hours", "text", "Mon–Fri 8am–5pm, on-call after hours"],
  ["office_headcount", "Office / administrative staff count", "text", "e.g. 4 (owner, coordinator, 2 office)"],
  ["languages", "Languages spoken by staff", "text", "e.g. English, Spanish"],
  ["extra", "Anything else the audit AI should always know", "area", "e.g. We serve Bucks County; founded 2019; ACHC-accredited…"],
];

export default function SettingsPage() {
  const [facts, setFacts] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pool, setPool] = useState(null);
  const [newQ, setNewQ] = useState("");
  const [poolBusy, setPoolBusy] = useState(false);
  const [extras, setExtras] = useState(null);
  const [extrasBusy, setExtrasBusy] = useState(false);
  const [flags, setFlags] = useState(null);
  const [rewards, setRewards] = useState(null);
  const [pointsCfg, setPointsCfg] = useState(null);
  const [redemptions, setRedemptions] = useState(null);
  const [codeBatch, setCodeBatch] = useState({ label: "", codes: "" });
  const [importBusy, setImportBusy] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPlan, setImportPlan] = useState(null);
  const uploadImport = async (f, confirm) => {
    setImportBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(`/admin/data-import${confirm ? "?confirm=RESTORE" : ""}`, fd, { timeout: 600000 });
      return data;
    } catch (e) { toast.error(formatApiError(e)); return null; }
    finally { setImportBusy(false); }
  };
  const previewImport = async (f) => {
    const data = await uploadImport(f, false);
    if (data) { setImportFile(f); setImportPlan(data); }
  };
  const runImport = async () => {
    const data = await uploadImport(importFile, true);
    if (data) {
      toast.success(`Restore complete — ${Object.values(data.restored || {}).reduce((a, b) => a + b, 0).toLocaleString()} records loaded`);
      setImportPlan(null); setImportFile(null);
    }
  };
  const loadPointsCfg = () => api.get("/admin/settings/points").then((r) => setPointsCfg(r.data)).catch(() => setPointsCfg(null));
  const loadRedemptions = () => api.get("/admin/points/redemptions").then((r) => setRedemptions(r.data.redemptions || [])).catch(() => setRedemptions([]));
  const savePointsCfg = async () => {
    try {
      await api.put("/admin/settings/points", { enabled: !!pointsCfg.enabled, rules: pointsCfg.rules, catalog: pointsCfg.catalog });
      toast.success("Points & swag shop saved");
      loadPointsCfg();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const addCodes = async () => {
    try {
      const { data } = await api.post("/admin/points/codes", codeBatch);
      toast.success(`${data.added} codes added to the pool`);
      setCodeBatch({ label: "", codes: "" });
      loadPointsCfg();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const fulfill = async (rid, codeId) => {
    try {
      await api.post(`/admin/points/redemptions/${rid}/fulfill`, { code_id: codeId });
      toast.success("Fulfilled — the staff member can see their code in the app");
      loadRedemptions(); loadPointsCfg();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const loadRewardsSettings = () => api.get("/admin/settings/wrap-rewards").then((r) => setRewards(r.data)).catch(() => setRewards(null));
  const saveRewards = async (r) => {
    try {
      await api.put("/admin/settings/wrap-rewards", { enabled: !!r.enabled, daily_spin: !!r.daily_spin, prizes: r.prizes });
      toast.success("Wrap-up rewards saved");
      loadRewardsSettings();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const saveFlags = async (f) => {
    setFlags(f);
    try {
      await api.put("/settings/features", { careplan_acks: !!f.careplan_acks, financials: !!f.financials, orientation_gate: !!f.orientation_gate, wrap_ups: !!f.wrap_ups });
      toast.success(f.careplan_acks ? "Care plan acknowledgments ON" : "Care plan acknowledgments OFF — nobody will be flagged");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => {
    api.get("/settings/agency-facts").then((r) => setFacts(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/settings/shift-note-questions").then((r) => setPool(r.data.rotating || [])).catch(() => setPool([]));
    api.get("/admin/settings/hiring-extras").then((r) => setExtras(r.data)).catch(() => setExtras({ fbi_service_code: "", fbi_facility_id: "", reference_autosend: false }));
    api.get("/settings/features").then((r) => setFlags(r.data)).catch(() => setFlags({ careplan_acks: false }));
    loadRewardsSettings();
    loadPointsCfg();
    loadRedemptions();
  }, []);

  const saveExtras = async () => {
    setExtrasBusy(true);
    try {
      await api.post("/admin/settings/hiring-extras", extras);
      toast.success("Hiring settings saved");
    } catch (e) { toast.error(formatApiError(e)); }
    setExtrasBusy(false);
  };

  const savePool = async (next) => {
    setPoolBusy(true);
    try {
      const { data } = await api.put("/settings/shift-note-questions", { rotating: next });
      setPool(data.rotating);
      toast.success("Wrap-up questions saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPoolBusy(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/settings/agency-facts", facts);
      toast.success("Saved — the audit AI will use these on every future answer");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (!facts) return <div className="flex justify-center py-24"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>;

  return (
    <div className="max-w-3xl" data-testid="settings-page">
      <h1 className="font-heading text-4xl font-semibold tracking-tight text-stone-900">Settings</h1>
      <p className="mt-1 text-sm text-stone-500">Agency-wide configuration. Everything here is stored and reused — set it once.</p>

      <div className="mt-6 rounded-xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="agency-facts-card">
        <div className="flex items-center gap-2">
          <Buildings size={18} className="text-primary" />
          <p className="font-heading text-sm font-semibold text-stone-900">Agency facts</p>
        </div>
        <p className="mt-1 text-xs text-stone-500">
          The audit AI reads these every time it answers a surveyor question — so it never has to guess or leave a gap on
          factual questions like hours, headcount, classifications, or languages.
        </p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          {FIELDS.map(([key, label, kind, ph]) => (
            <div key={key} className={`space-y-1.5 ${kind === "area" ? "sm:col-span-2" : ""}`}>
              <Label>{label}</Label>
              {kind === "area" ? (
                <Textarea rows={3} value={facts[key] || ""} onChange={(e) => setFacts({ ...facts, [key]: e.target.value })} placeholder={ph} className="border-stone-300" data-testid={`fact-${key}`} />
              ) : (
                <Input value={facts[key] || ""} onChange={(e) => setFacts({ ...facts, [key]: e.target.value })} placeholder={ph} className="border-stone-300" data-testid={`fact-${key}`} />
              )}
            </div>
          ))}
          <div className="space-y-1.5">
            <Label>Direct care workers are…</Label>
            <select value={facts.dcw_classification || ""} onChange={(e) => setFacts({ ...facts, dcw_classification: e.target.value })}
              className="h-9 w-full rounded-md border border-stone-300 bg-white px-3 text-sm" data-testid="fact-dcw-classification">
              <option value="">— not set —</option><option>W2 employees</option><option>1099 independent contractors</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <Label>Office staff are…</Label>
            <select value={facts.office_classification || ""} onChange={(e) => setFacts({ ...facts, office_classification: e.target.value })}
              className="h-9 w-full rounded-md border border-stone-300 bg-white px-3 text-sm" data-testid="fact-office-classification">
              <option value="">— not set —</option><option>W2 employees</option><option>1099 independent contractors</option>
            </select>
          </div>
        </div>
        <Button onClick={save} disabled={busy} className="mt-4 bg-primary text-primary-foreground hover:bg-stone-900" data-testid="agency-facts-save">
          {busy ? "Saving…" : "Save agency facts"}
        </Button>
      </div>

      <div className="mt-6 rounded-xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="shift-questions-card">
        <div className="flex items-center gap-2">
          <ClipboardText size={18} className="text-primary" />
          <p className="font-heading text-sm font-semibold text-stone-900">Shift wrap-up questions</p>
        </div>
        <p className="mt-1 text-xs text-stone-500">
          Two of these rotating questions are picked each day for every wrap-up note — broad, gentle prompts that keep notes fresh.
          The three safety questions (complaints / declined tasks / incidents) are always asked and can't be removed.
          Client-specific questions are set on each client's record.
        </p>
        {pool === null ? null : (
          <>
            <div className="mt-4 space-y-2">
              {pool.map((q, i) => (
                <div key={i} className="flex items-center gap-2 rounded-lg border border-stone-200 px-3 py-2" data-testid={`pool-q-${i}`}>
                  <p className="flex-1 text-sm text-stone-800">{q}</p>
                  <button onClick={() => savePool(pool.filter((_, j) => j !== i))} disabled={poolBusy}
                    className="text-stone-400 hover:text-[#C0392B]" data-testid={`pool-q-delete-${i}`}><Trash size={15} /></button>
                </div>
              ))}
            </div>
            <div className="mt-3 flex gap-2">
              <Input value={newQ} onChange={(e) => setNewQ(e.target.value)} placeholder="Add a broad question, e.g. What did the client talk about most today?"
                className="border-stone-300" data-testid="pool-new-question" />
              <Button onClick={() => { if (!newQ.trim()) return; savePool([...pool, newQ.trim()]); setNewQ(""); }} disabled={poolBusy}
                className="shrink-0 bg-primary text-primary-foreground hover:bg-stone-900" data-testid="pool-add-question">
                <Plus size={15} className="mr-1" /> Add
              </Button>
            </div>
          </>
        )}
      </div>

      <div className="mt-6 rounded-xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="hiring-extras-card">
        <div className="flex items-center gap-2">
          <Gear size={18} className="text-primary" />
          <p className="font-heading text-sm font-semibold text-stone-900">Hiring & background checks</p>
        </div>
        <p className="mt-1 text-xs text-stone-500">
          IdentoGO codes print on the FBI instruction sheet that goes to applicants who need the fingerprint check.
        </p>
        {extras && (
          <div className="mt-4 space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>IdentoGO service code</Label>
                <Input value={extras.fbi_service_code || ""} onChange={(e) => setExtras({ ...extras, fbi_service_code: e.target.value })} placeholder="e.g. 1RA…" className="border-stone-300" data-testid="fbi-service-code" />
              </div>
              <div className="space-y-1.5">
                <Label>Facility / agency ID</Label>
                <Input value={extras.fbi_facility_id || ""} onChange={(e) => setExtras({ ...extras, fbi_facility_id: e.target.value })} placeholder="Optional" className="border-stone-300" data-testid="fbi-facility-id" />
              </div>
            </div>
            <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-stone-200 p-3" data-testid="reference-autosend-row">
              <input type="checkbox" checked={!!extras.reference_autosend}
                onChange={(e) => setExtras({ ...extras, reference_autosend: e.target.checked })}
                className="mt-0.5 h-4 w-4 accent-[#4A235A]" data-testid="reference-autosend-toggle" />
              <span>
                <span className="block text-sm font-medium text-stone-800">Auto-send reference requests on application</span>
                <span className="mt-0.5 block text-xs text-stone-500">
                  When an application comes in, each listed reference gets the 6-question reference form by email automatically.
                  {extras.reference_autosend
                    ? " ON — will send as soon as outgoing email is also enabled."
                    : " OFF — requests are prepared and wait for you (outgoing email is globally off until you enable it)."}
                </span>
              </span>
            </label>
            <Button onClick={saveExtras} disabled={extrasBusy} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="hiring-extras-save">
              {extrasBusy ? "Saving…" : "Save hiring settings"}
            </Button>
          </div>
        )}
      </div>

      <div className="mt-6 rounded-xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="feature-switches-card">
        <div className="flex items-center gap-2">
          <Gear size={18} className="text-primary" />
          <p className="font-heading text-sm font-semibold text-stone-900">Feature switches</p>
        </div>
        <p className="mt-1 text-xs text-stone-500">Whole features you can turn on or off. While a switch is off, nobody is asked or flagged for it anywhere.</p>
        {flags && (
          <label className="mt-4 flex cursor-pointer items-start gap-3 rounded-lg border border-stone-200 p-3" data-testid="careplan-acks-row">
            <input type="checkbox" checked={!!flags.careplan_acks}
              onChange={(e) => saveFlags({ ...flags, careplan_acks: e.target.checked })}
              className="mt-0.5 h-4 w-4 accent-[#4A235A]" data-testid="careplan-acks-toggle" />
            <span>
              <span className="block text-sm font-medium text-stone-800">Care plan acknowledgments</span>
              <span className="mt-0.5 block text-xs text-stone-500">
                {flags.careplan_acks
                  ? " ON — caregivers are asked to read & sign the care plan, and missing signatures show up in reports."
                  : " OFF — no one is asked or flagged for care plan signatures. Turn on when you're ready to enforce it."}
              </span>
            </span>
          </label>
        )}
        {flags && (
          <label className="mt-3 flex cursor-pointer items-start gap-3 rounded-lg border border-stone-200 p-3" data-testid="orientation-gate-row">
            <input type="checkbox" checked={!!flags.orientation_gate}
              onChange={(e) => saveFlags({ ...flags, orientation_gate: e.target.checked })}
              className="mt-0.5 h-4 w-4 accent-[#4A235A]" data-testid="orientation-gate-toggle" />
            <span>
              <span className="block text-sm font-medium text-stone-800">Orientation compliance flag</span>
              <span className="mt-0.5 block text-xs text-stone-500">
                {flags.orientation_gate
                  ? " ON — staff who haven't finished the orientation checklist show as out of compliance."
                  : " OFF — nobody is flagged for orientation. Turn on when you're ready to enforce it."}
              </span>
            </span>
          </label>
        )}
        {flags && (
          <label className="mt-3 flex cursor-pointer items-start gap-3 rounded-lg border border-stone-200 p-3" data-testid="wrap-ups-row">
            <input type="checkbox" checked={!!flags.wrap_ups}
              onChange={(e) => saveFlags({ ...flags, wrap_ups: e.target.checked })}
              className="mt-0.5 h-4 w-4 accent-[#4A235A]" data-testid="wrap-ups-toggle" />
            <span>
              <span className="block text-sm font-medium text-stone-800">Shift wrap-up reminders</span>
              <span className="mt-0.5 block text-xs text-stone-500">
                {flags.wrap_ups
                  ? " ON — staff see which recent shifts still need a wrap-up note."
                  : " OFF — nothing shows as past due for wrap-ups. Turn on when you're ready to enforce daily wrap-ups."}
              </span>
            </span>
          </label>
        )}
      </div>

      <div className="mt-6 rounded-xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="wrap-rewards-card">
        <div className="flex items-center gap-2">
          <Gear size={18} className="text-primary" />
          <p className="font-heading text-sm font-semibold text-stone-900">Wrap-up rewards (the wheel)</p>
        </div>
        <p className="mt-1 text-xs text-stone-500">
          Every on-time wrap-up earns a spin (once a day), and a perfect week earns a bonus spin. Heavier weight = more likely.
        </p>
        {rewards && (
          <div className="mt-4 space-y-4">
            <label className="flex items-center gap-3" data-testid="wrap-rewards-enabled-row">
              <input type="checkbox" checked={!!rewards.enabled}
                onChange={(e) => setRewards({ ...rewards, enabled: e.target.checked })}
                className="h-4 w-4 accent-[#4A235A]" data-testid="wrap-rewards-enabled" />
              <span className="text-sm font-medium text-stone-800">Wheel is on</span>
            </label>
            <label className="flex items-center gap-3" data-testid="wrap-rewards-daily-row">
              <input type="checkbox" checked={!!rewards.daily_spin}
                onChange={(e) => setRewards({ ...rewards, daily_spin: e.target.checked })}
                className="h-4 w-4 accent-[#4A235A]" data-testid="wrap-rewards-daily" />
              <span className="text-sm font-medium text-stone-800">Daily spins — every on-time wrap-up earns a spin (max 1 per day)</span>
            </label>
            <div className="space-y-2">
              {rewards.prizes.map((p, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input value={p.label}
                    onChange={(e) => setRewards({ ...rewards, prizes: rewards.prizes.map((x, j) => j === i ? { ...x, label: e.target.value } : x) })}
                    className="border-stone-300" data-testid={`prize-label-${i}`} />
                  <Input type="number" min={1} max={10} value={p.weight}
                    onChange={(e) => setRewards({ ...rewards, prizes: rewards.prizes.map((x, j) => j === i ? { ...x, weight: parseInt(e.target.value) || 1 } : x) })}
                    className="w-20 border-stone-300" data-testid={`prize-weight-${i}`} />
                  <button onClick={() => setRewards({ ...rewards, prizes: rewards.prizes.filter((_, j) => j !== i) })}
                    className="text-xs text-stone-400 hover:text-red-600" data-testid={`prize-remove-${i}`}>remove</button>
                </div>
              ))}
              <button onClick={() => setRewards({ ...rewards, prizes: [...rewards.prizes, { label: "", weight: 1 }] })}
                className="text-xs font-medium text-[#4A235A] hover:underline" data-testid="prize-add">+ add a prize</button>
            </div>
            <Button onClick={() => saveRewards(rewards)} className="bg-primary text-primary-foreground" data-testid="save-wrap-rewards">Save rewards</Button>
            <div className="border-t border-stone-100 pt-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-stone-400">Recent winners</p>
              {(rewards.recent_wins || []).length > 0 ? (
                <ul className="mt-2 space-y-1" data-testid="recent-winners">
                  {rewards.recent_wins.map((w, i) => (
                    <li key={i} className="text-sm text-stone-600">{w.name} — <span className="font-medium">{w.prize}</span> <span className="text-xs text-stone-400">{(w.at || "").slice(0, 10)}</span></li>
                  ))}
                </ul>
              ) : (
                <p className="mt-2 text-xs text-stone-400" data-testid="recent-winners">No winners yet — first perfect week earns the first spin.</p>
              )}
            </div>
          </div>
        )}
      </div>

      <WrapQuestionBank />

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <Link to="/billing" className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md" data-testid="settings-billing-link">
          <div className="flex items-center gap-2"><Money size={18} className="text-primary" /><p className="text-sm font-semibold text-stone-900">Billing settings</p></div>
          <p className="mt-1 text-xs text-stone-500">Venmo/Zelle instructions, invoice note, private-pay client rates.</p>
        </Link>
        <Link to="/notifications" className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md" data-testid="settings-notifications-link">
          <div className="flex items-center gap-2"><Bell size={18} className="text-primary" /><p className="text-sm font-semibold text-stone-900">Notifications</p></div>
          <p className="mt-1 text-xs text-stone-500">Outbound message approvals and notification preferences.</p>
        </Link>
      </div>

      <div className="mt-6 rounded-xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="data-backup-card">
        <div className="flex items-center gap-2">
          <Gear size={18} className="text-primary" />
          <p className="font-heading text-sm font-semibold text-stone-900">Data backup &amp; restore</p>
        </div>
        <p className="mt-1 text-xs text-stone-500">
          Download a complete copy of everything in the system (clients, visits, staff, care plans, intake answers — one zip).
          After publishing a fresh production app, upload the same zip here to load it all back. Restoring is safe to run
          more than once — it updates matching records and never deletes anything. Keep the zip private: it contains client information.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <a href={`${api.defaults.baseURL}/admin/data-export.zip`} download
            className="inline-flex h-9 items-center rounded-md bg-[#4A235A] px-4 text-sm font-medium text-white hover:bg-stone-900"
            data-testid="data-export-button">
            Download all data (zip)
          </a>
          <label className="inline-flex h-9 cursor-pointer items-center rounded-md border border-stone-300 bg-white px-4 text-sm font-medium text-stone-700 hover:bg-stone-50" data-testid="data-import-label">
            {importBusy ? "Checking file…" : "Choose export zip to restore…"}
            <input type="file" accept=".zip" className="hidden" disabled={importBusy}
              onChange={(e) => { const f = e.target.files && e.target.files[0]; if (f) previewImport(f); e.target.value = ""; }}
              data-testid="data-import-file" />
          </label>
        </div>
        {importPlan && (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4" data-testid="data-import-plan">
            <p className="text-sm font-semibold text-stone-900">
              Ready to restore {importPlan.collections.reduce((n, c) => n + c.documents, 0).toLocaleString()} records across {importPlan.collections.length} collections
            </p>
            <p className="mt-1 text-xs text-stone-600">{importPlan.note}</p>
            <div className="mt-3 flex gap-2">
              <Button onClick={runImport} disabled={importBusy} className="bg-[#4A235A] text-white" data-testid="data-import-confirm">
                {importBusy ? "Restoring…" : "Restore now"}
              </Button>
              <Button variant="outline" onClick={() => { setImportPlan(null); setImportFile(null); }} data-testid="data-import-cancel">Cancel</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
